/**
 * Language Switcher Component
 *
 * Dropdown to switch between supported languages
 */

import { Check, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDismissableMenu } from '@/hooks/use-dismissable-menu'
import { changeLanguage, supportedLanguages } from '@/i18n'
import { Button } from './ui'

interface LanguageSwitcherProps {
	variant?: 'dropdown' | 'buttons'
	className?: string
}

export function LanguageSwitcher({ variant = 'dropdown', className = '' }: LanguageSwitcherProps) {
	const { i18n, t } = useTranslation()
	const currentLang = i18n.language
	const menu = useDismissableMenu()

	if (variant === 'buttons') {
		return (
			<div className={`flex gap-1 ${className}`}>
				{supportedLanguages.map((lang) => (
					<button
						key={lang.code}
						type="button"
						onClick={() => changeLanguage(lang.code)}
						className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
							currentLang === lang.code ? 'bg-primary text-primary-content' : 'hover:bg-base-200'
						}`}
						aria-current={currentLang === lang.code ? 'true' : undefined}
					>
						<span className="mr-1.5">{lang.flag}</span>
						{lang.code.toUpperCase()}
					</button>
				))}
			</div>
		)
	}

	// Explicit open state, not DaisyUI's `:focus-within` dropdown: picking a
	// language has to close the menu (see use-dismissable-menu).
	return (
		<div ref={menu.containerRef} className={`relative ${className}`}>
			<Button
				ref={menu.triggerRef}
				variant="ghost"
				size="sm"
				aria-label={t('settings.language')}
				{...menu.triggerProps}
			>
				<Globe size={20} />
				<span className="hidden sm:inline text-sm">
					{supportedLanguages.find((l) => l.code === currentLang)?.flag}
				</span>
			</Button>
			{menu.open && (
				<ul className="absolute right-0 top-full z-50 menu p-2 shadow-soft-lg bg-base-100 rounded-xl w-48 mt-2">
					{supportedLanguages.map((lang) => (
						<li key={lang.code}>
							<button
								type="button"
								onClick={() => {
									changeLanguage(lang.code)
									menu.closeAndRefocus()
								}}
								className={`flex items-center gap-3 ${currentLang === lang.code ? 'active' : ''}`}
							>
								<span className="text-lg">{lang.flag}</span>
								<span className="flex-1">{lang.name}</span>
								{currentLang === lang.code && <Check size={16} className="text-primary" />}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
